// The single-profile export's progress dialogue (cee70a51e8) — `ShortlistSingleProfileExportService`
// shows the lists' own templates (`extension-alert-candidates-export-modal.tpl.html` /
// `extension-alert-client-contacts-export-modal.tpl.html`) with the lists' window class, so this is
// `ExportToAtsDialogue` driven by `SingleProfileExport` instead of by the lists' poll.
//
// ⚠️ THIS WRITES INTO THE CUSTOMER'S ATS. It starts the export on mount, ONCE — StrictMode's double
// effect is guarded, as `ExportToAtsModal`'s is — and nothing else in the profile may call the
// start endpoints.
import { useEffect, useRef, useState, type ReactElement } from 'react'
import {
  exportClientContactsToAtsList,
  exportStatus,
  exportToAtsList,
  type ExportListTarget,
  type ExportStartedResponse,
  type ExportStatusResponse,
} from '@woo-ext/api'
import type { ExportEntity } from './CandidateExportPanel.tsx'
import { ExportToAtsDialogue } from './ExportToAtsModal.tsx'
import { exportGoToAtsCopy, opensHotlistInAts } from './exportToAts.ts'
import {
  SingleProfileExport,
  singleProfileExportDialogueView,
  type SingleProfileExportOutcome,
  type SingleProfileExportView,
} from './singleProfileExport.ts'

export interface SingleProfileExportModalProps {
  entity: ExportEntity
  /** The profile's one record. */
  id: number
  /** The picker's choice, or `singleProfileNewListTarget(name)` when the ATS has no existing lists. */
  target: ExportListTarget
  /** `careero.atsName` — the Done button's label, and whether it opens the hotlist. */
  atsName?: string
  /** `openListInAts(outputParamsJson)` — Bullhorn only; the parent frame opens the list. */
  onOpenInAts: (outputParamsJson: unknown) => void
  /** The export's outcome — once. Unmount on it. */
  onSettled: (outcome: SingleProfileExportOutcome) => void
  /** The ✕: no outcome (see `SingleProfileExport.dismiss`). Unmount on it. */
  onDismissed: () => void
  /** Injected by tests; the real endpoints otherwise. */
  start?: () => Promise<ExportStartedResponse>
  status?: (exportMessageId: number) => Promise<ExportStatusResponse>
}

export function SingleProfileExportModal({
  entity,
  id,
  target,
  atsName,
  onOpenInAts,
  onSettled,
  onDismissed,
  start,
  status = exportStatus,
}: SingleProfileExportModalProps): ReactElement {
  const callbacks = useRef({ onOpenInAts, onSettled, onDismissed })
  useEffect(() => {
    callbacks.current = { onOpenInAts, onSettled, onDismissed }
  }, [onOpenInAts, onSettled, onDismissed])

  const [view, setView] = useState<SingleProfileExportView>({ open: true, inProgress: true, response: null })
  const [goToAtsCopy] = useState(() => exportGoToAtsCopy(atsName))

  // The request is fixed when the dialogue opens, and so is the run.
  const run = useRef<SingleProfileExport | undefined>(undefined)
  if (run.current === undefined) {
    const startExport =
      start ??
      (() =>
        entity === 'candidates'
          ? exportToAtsList({ candidateIds: [id], ...target })
          : exportClientContactsToAtsList({ clientContactIds: [id], ...target }))
    run.current = new SingleProfileExport({
      start: startExport,
      status,
      onChange: setView,
      onSettle: (outcome) => {
        callbacks.current.onSettled(outcome)
      },
    })
  }

  // ⚠️ STRICTMODE UNMOUNTS AND REMOUNTS THE SAME INSTANCE in development, synchronously. Disposing
  // on that simulated unmount would kill the run before its start call answers, so the dispose
  // waits a microtask and happens only if the component is still gone by then.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    const current = run.current
    // ONE START, EVER — `begin` is idempotent, so the second effect is a no-op.
    current?.begin()
    return () => {
      mounted.current = false
      queueMicrotask(() => {
        if (!mounted.current) current?.dispose()
      })
    }
  }, [])

  return (
    <ExportToAtsDialogue
      open={view.open}
      view={singleProfileExportDialogueView(view)}
      goToAtsCopy={goToAtsCopy}
      onNotifyMe={() => {
        run.current?.notifyMe()
      }}
      onGoToAts={() => {
        const response = run.current?.current.response
        run.current?.goToAts()
        if (opensHotlistInAts(atsName)) callbacks.current.onOpenInAts(response?.outputParamsJson)
      }}
      onDismiss={() => {
        run.current?.dismiss()
        callbacks.current.onDismissed()
      }}
    />
  )
}
